/**
 * Google Analytics 4 + Google Ads conversion tracking via gtag.js
 *
 * Loads the gtag snippet once, then exposes helpers for standard
 * e-commerce events that mirror the Meta Pixel integration.
 */

import { hasOptedOut } from "./consent";
import { isProductionHost } from "./is-production-host";
const GA4_MEASUREMENT_ID = "G-WNFX6BNTKK";
const GOOGLE_ADS_ID = "AW-18037958444";

// ─── gtag type shim ─────────────────────────────────────────────────
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// ─── Load gtag.js (runs once) ───────────────────────────────────────
let gtagLoaded = false;

export function initGtag() {
  if (gtagLoaded || typeof window === "undefined" || !isProductionHost()) return;
  gtagLoaded = true;

  // Canonical Google snippet: dataLayer must receive an IArguments-like value.
  // Using `function(){dataLayer.push(arguments)}` (NOT a rest-array wrapper) is
  // required — gtag.js internally treats each dataLayer entry as `arguments`,
  // and a plain Array can cause commands to be silently ignored.
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-rest-params
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments as unknown as unknown[]);
  }
  window.gtag = gtag as unknown as (...args: unknown[]) => void;

  // Load gtag.js FIRST so subsequent commands flush immediately once it parses.
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // US opt-out (CCPA/CPRA): Consent Mode v2 defaults BEFORE config.
  const optedOut = hasOptedOut();
  window.gtag("consent", "default", {
    ad_storage: optedOut ? "denied" : "granted",
    ad_user_data: optedOut ? "denied" : "granted",
    ad_personalization: optedOut ? "denied" : "granted",
    analytics_storage: "granted",
    security_storage: "granted",
  });
  if (optedOut) {
    window.gtag("set", "ads_data_redaction", true);
  }

  window.gtag("js", new Date());
  // Let GA4 send its own automatic page_view on config — this guarantees at
  // least one /g/collect beacon fires as soon as the tag loads.
  window.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: true });
  window.gtag("config", GOOGLE_ADS_ID);
}

// ─── Helpers ────────────────────────────────────────────────────────

export function gtagPageView(path?: string) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path || window.location.pathname,
  });
}

export function gtagViewItem(params: {
  item_id: string;
  item_name: string;
  price: number;
  currency?: string;
}) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "view_item", {
    currency: params.currency || "USD",
    value: params.price,
    items: [
      {
        item_id: params.item_id,
        item_name: params.item_name,
        price: params.price,
        quantity: 1,
      },
    ],
  });
}

export function gtagAddToCart(params: {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  currency?: string;
}) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "add_to_cart", {
    currency: params.currency || "USD",
    value: params.price * params.quantity,
    items: [
      {
        item_id: params.item_id,
        item_name: params.item_name,
        price: params.price,
        quantity: params.quantity,
      },
    ],
  });
}

export function gtagBeginCheckout(params: {
  items: Array<{ item_id: string; item_name: string; price: number; quantity: number }>;
  value: number;
  currency?: string;
}) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "begin_checkout", {
    currency: params.currency || "USD",
    value: params.value,
    items: params.items,
  });
}
